/**
 * Auto-extracts up to N (default 3, configurable via data-product-usps-count)
 * technical/material USP claims from a product's body_html and renders them
 * into [data-product-usps-auto]. Only runs when the product has no
 * product.metafields.custom.usps value — see product-usps.liquid for the
 * priority order and the reasoning behind why this lives in JS rather than
 * Liquid (no real pattern-matching in Liquid).
 *
 * Heuristic, not language understanding: a fixed dictionary of technical-
 * claim signals (named fabric technologies, construction methods, numeric
 * weight claims, moisture/dryness/stretch/breathability terms), scored by
 * specificity (a number or named technology outranks a plain adjective).
 * Tuned against this catalog's real product descriptions — will miss or
 * misrank claims phrased outside that dictionary. Prefer the metafield for
 * any product where the auto-picked claims aren't the right ones.
 *
 * Pockets are handled separately and always claim slots first, ahead of
 * fabric/technical claims — strong purchase driver for running gear. Can
 * surface MORE THAN ONE distinct pocket claim (e.g. a phone pocket AND a
 * gel pocket) when the description genuinely describes more than one —
 * see extractPocketClaims below.
 */
(function () {
  'use strict';

  var ICONS = {
    weight:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/><line x1="17.5" y1="15" x2="9" y2="15"/></svg>',
    wind:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9.59 4.59A2 2 0 1 1 11 8H2"/><path d="M12.59 19.41A2 2 0 1 0 14 16H2"/><path d="M17.73 7.73A2.5 2.5 0 1 1 19.5 12H2"/></svg>',
    droplet:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/></svg>',
    stretch:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12h2l2-6 3 12 3-12 3 12 3-12 2 6h2"/></svg>',
    dropletSlash:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/><line x1="4" y1="20" x2="20" y2="4"/></svg>',
    woven:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>',
    seam:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h18" stroke-dasharray="3 3"/><path d="M7 8v8M12 8v8M17 8v8" opacity="0.6"/></svg>',
    sparkle:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M18 6l-2.5 2.5M8.5 15.5L6 18"/></svg>',
    pocket:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M4 4h16v10a6 6 0 0 1-6 6h-4a6 6 0 0 1-6-6V4z"/><path d="M4 4l8 6 8-6"/></svg>',
    action:
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="miter"><path d="M14 1 9 13h4l-2 10 6-13h-4z"/></svg>'
  };
  var GENERIC_ICON = ICONS.woven;

  function titleCase(s) {
    return s
      .split(' ')
      .map(function (word) {
        return word
          .split('-')
          .map(function (part) {
            return /^\d/.test(part) ? part.toUpperCase() : part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
          })
          .join('-');
      })
      .join(' ');
  }

  function stripHtml(html) {
    return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  function extractFeatureItems(html) {
    var items = [];
    var ulMatch = html.match(/<ul>([\s\S]*?)<\/ul>/i);
    if (ulMatch) {
      var liRe = /<li>([\s\S]*?)<\/li>/gi;
      var m;
      while ((m = liRe.exec(ulMatch[1])) !== null) {
        items.push(stripHtml(m[1]));
      }
    }
    return items;
  }

  var CATEGORIES = [
    {
      key: 'weight',
      icon: ICONS.weight,
      label: function (m) { return 'Ultra Lightweight (' + m.grams + 'g)'; },
      // Requires an actual grams figure — not just the word "lightweight"
      // appearing somewhere in the copy. That word alone is common,
      // incidental marketing filler ("remaining lightweight, breathable,
      // and comfortable") that doesn't back up a quantified "Ultra
      // Lightweight" claim; a real number (e.g. "60g") does. Confirmed
      // against the catalog: the swimsuit and compression socks both say
      // "lightweight" in passing with no weight figure at all, while the
      // fitted tee and tracksuit state one explicitly (60g / 319g).
      test: function (text) {
        var gramsMatch = text.match(/\b(\d{2,4})\s?g\b/i);
        if (!gramsMatch) return null;
        return { score: 3, grams: gramsMatch[1] };
      }
    },
    {
      key: 'quick_dry_stretch_combo',
      icon: ICONS.wind,
      label: function () { return 'Quick-Dry Stretch Fabric'; },
      test: function (text) {
        var hasQuickDry = /quick-dry(ing)?/i.test(text);
        var hasStretch = /\bstretch\b/i.test(text);
        if (!hasQuickDry || !hasStretch) return null;
        return { score: 3 };
      }
    },
    {
      key: 'quick_dry',
      icon: ICONS.wind,
      label: function () { return 'Quick-Dry Fabric'; },
      test: function (text) {
        if (!/quick-dry(ing)?/i.test(text)) return null;
        return { score: 2 };
      }
    },
    {
      key: 'moisture_wicking',
      icon: ICONS.droplet,
      label: function () { return 'Moisture-Wicking Fabric'; },
      test: function (text) {
        if (!/moisture-wicking/i.test(text)) return null;
        return { score: 2 };
      }
    },
    {
      key: 'stretch',
      icon: ICONS.stretch,
      label: function () { return 'Stretch Construction'; },
      test: function (text) {
        if (!/\bstretch\b/i.test(text) && !/\bcompression\b/i.test(text)) return null;
        return { score: 1 };
      }
    },
    {
      key: 'water_repellent',
      icon: ICONS.dropletSlash,
      // "Water-resistant" and "water-repellent" show up interchangeably
      // across the catalog (e.g. the race bag says "resistant", the cap
      // says "repels light rain") — label matches whichever the product
      // actually said, rather than always defaulting to one wording.
      label: function (m) { return m.resistant ? 'Water-Resistant Fabric' : 'Water-Repellent Fabric'; },
      test: function (text) {
        if (/water-repellent/i.test(text) || /repels?\s+(light\s+)?rain/i.test(text)) return { score: 2, resistant: false };
        if (/water-resistant/i.test(text)) return { score: 2, resistant: true };
        return null;
      }
    },
    {
      key: 'material_named',
      icon: ICONS.woven,
      label: function (m) { return m.material; },
      // Only surface a named-material claim when it's RECYCLED material —
      // plain "100% Polyester" / "Nylon-Spandex Blend" isn't a real
      // differentiator (most of this catalog is some synthetic blend), so
      // it was cluttering slots with a non-claim. "70% Recycled
      // Polyester" is a genuine, worth-surfacing sustainability claim.
      // Checked via proximity to "recycled" (not a fixed word order)
      // since real copy phrases this a few ways — confirmed against this
      // catalog's actual descriptions before shipping ("Made from a
      // premium recycled polyester blend" vs. "Made from 100% nylon",
      // "Constructed from 100% polyester", etc.).
      test: function (text) {
        var pctMatch = text.match(/\b(\d{2,3}%)\s+(nylon|polyester|spandex|cotton|merino wool)\b/i);
        if (pctMatch) {
          var pctContext = text.slice(Math.max(0, pctMatch.index - 25), pctMatch.index + pctMatch[0].length + 10);
          if (!/\brecycled\b/i.test(pctContext)) return null;
          return { score: 3, material: pctMatch[1] + ' Recycled ' + titleCase(pctMatch[2]) + ' Construction' };
        }
        var namedMatch = text.match(/\b(nylon|polyester|spandex|cotton|merino wool)\b/i);
        if (namedMatch) {
          var namedContext = text.slice(Math.max(0, namedMatch.index - 25), namedMatch.index + namedMatch[0].length + 10);
          if (!/\brecycled\b/i.test(namedContext)) return null;
          return { score: 2, material: 'Recycled ' + titleCase(namedMatch[1]) + ' Blend' };
        }
        return null;
      }
    },
    {
      key: 'construction_method',
      icon: ICONS.seam,
      label: function (m) { return m.construction; },
      test: function (text) {
        var named = text.match(/\b(laser-cut|bonded|seamless|3d woven|open-back construction)\b/i);
        if (!named) return null;
        var label = titleCase(named[1]);
        if (label.indexOf('Construction') === -1) label += ' Construction';
        return { score: 3, construction: label };
      }
    },
    {
      key: 'reflective',
      icon: ICONS.sparkle,
      label: function () { return 'Reflective Safety Details'; },
      test: function (text) {
        if (!/reflective/i.test(text)) return null;
        return { score: 1 };
      }
    },
    {
      key: 'breathable',
      icon: ICONS.wind,
      label: function () { return 'Breathable Design'; },
      test: function (text) {
        if (!/\bbreathable\b|\bbreathability\b/i.test(text)) return null;
        return { score: 1 };
      }
    }
  ];

  // Used ONLY to backfill remaining slots when pockets + CATEGORIES above
  // don't reach topN (some descriptions genuinely only support 1-2 strong
  // claims, e.g. the tracksuit, once material/weight are held to a real
  // textual bar) — never competes with the primary claims above, and
  // never reintroduces a word-only "lightweight" mention (that's the
  // exact overclaim this replaced weight's old behavior to avoid).
  // Softer signals, still grounded in the actual description text, not
  // invented copy.
  var FALLBACK_CATEGORIES = [
    {
      key: 'durable',
      icon: ICONS.seam,
      label: function () { return 'Built to Last'; },
      test: function (text) {
        if (!/\bdurable\b|\bdurability\b/i.test(text)) return null;
        return { score: 1 };
      }
    },
    {
      key: 'versatile',
      icon: ICONS.sparkle,
      label: function () { return 'Versatile Everyday Wear'; },
      test: function (text) {
        if (!/\bversatile\b/i.test(text)) return null;
        return { score: 1 };
      }
    },
    {
      key: 'comfort',
      icon: ICONS.stretch,
      label: function () { return 'All-Day Comfort'; },
      test: function (text) {
        if (!/\bcomfort(able)?\b/i.test(text)) return null;
        return { score: 1 };
      }
    }
  ];

  // Pockets are a strong purchase driver for running gear, so — per the
  // brief — a detected pocket claim always takes one of the top slots
  // rather than competing on the same 1-3 specificity score as fabric/
  // construction claims. Handled as its own pass, not a CATEGORIES entry:
  // requiring "pocket" to co-occur with a storage-context word (gel/phone/
  // mesh/zip/side/back/etc.) in the SAME sentence avoids false positives
  // like "folds into a pocket" (the race bag's packability claim, not a
  // garment feature) — confirmed against the real catalog before shipping.
  //
  // Surfaces MULTIPLE distinct pocket claims (e.g. a phone pocket AND a gel
  // pocket), not just one — real running-apparel descriptions genuinely
  // describe more than one pocket type (e.g. this catalog's tights: a back
  // "pocket for phone" and separate side "pockets for gels"), and both are
  // meaningful, distinct purchase drivers, not duplicates of the same claim.
  // Detected per FEATURE-LIST ITEM (li), not over the flattened full-text —
  // items in a <ul> don't end in sentence punctuation once HTML-stripped,
  // so two adjacent <li>s collapse into what looks like one run-on
  // "sentence" if split on .!?, making it impossible to tell which
  // modifier (mesh/secure/zip) belongs to which pocket mention. Falls back
  // to sentence-splitting only for the rare product with no <ul> at all.
  var POCKET_RE = /\bpockets?\b/i;
  var POCKET_CONTEXT_RE = /\b(gel|phone|mesh|zip|zipped|zippered|side|back|essentials|key|card|storage)\b/i;
  var POCKET_TYPES = [
    { type: 'Gel', re: /\bgels?\b/i },
    { type: 'Phone', re: /\bphone\b/i },
    { type: 'Key', re: /\bkeys?\b/i },
    { type: 'Card', re: /\bcards?\b/i }
  ];

  function pocketModifierPrefix(text) {
    if (/\b(anti-bounce|minimal bounce|no bounce|without bounce)\b/i.test(text)) return 'Anti-Bounce';
    if (/\blarge\b/i.test(text)) return 'Large';
    if (/\bsecure\b/i.test(text)) return 'Secure';
    if (/\bmesh\b/i.test(text)) return 'Mesh';
    if (/\bzip(ped|pered)?\b/i.test(text)) return 'Zip';
    return '';
  }

  // Real copy in this catalog states an explicit count right on the same
  // feature line, e.g. "2 secure mesh side pockets for gels or small
  // items" — extracted from THIS unit only (not the wider fullText, unlike
  // the modifier above), since a count is a specific local fact and
  // pulling one from unrelated prose elsewhere could misattribute it.
  // Falls back to no count (singular "Pocket" label, unchanged prior
  // behavior) when the line doesn't state one.
  var COUNT_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5 };
  function pocketCount(unit) {
    var m = unit.match(/\b(\d+|one|two|three|four|five)\b\s*(?:secure\s+|mesh\s+|zip(?:ped|pered)?\s+|side\s+|back\s+)*pockets?\b/i);
    if (!m) return null;
    var raw = m[1].toLowerCase();
    return COUNT_WORDS[raw] || parseInt(raw, 10);
  }

  function extractPocketClaims(bodyHtml, fullText, maxClaims) {
    var featureItems = extractFeatureItems(bodyHtml);
    var units = featureItems.length ? featureItems : fullText.split(/(?<=[.!?])\s+/);
    var pocketUnits = units.filter(function (u) {
      return POCKET_RE.test(u) && POCKET_CONTEXT_RE.test(u);
    });
    if (!pocketUnits.length) return [];

    var claimed = {};
    var claims = [];
    pocketUnits.forEach(function (unit) {
      POCKET_TYPES.forEach(function (t) {
        if (claimed[t.type] || !t.re.test(unit)) return;
        claimed[t.type] = true;
        // Modifier can come from this specific line OR the wider product
        // copy (e.g. "minimal bounce" describing the pocket system as a
        // whole in a separate prose sentence) — checked together, not
        // unit-first-then-fallback, so the priority order (anti-bounce >
        // large > secure > mesh > zip) is respected regardless of WHICH
        // of the two happens to contain the strongest signal. Checking
        // unit first in isolation would let a merely-local "secure" win
        // over a more compelling "anti-bounce" sitting in the shared
        // prose, just because of evaluation order — not because it's
        // actually the better claim.
        var prefix = pocketModifierPrefix(unit + ' ' + fullText);
        var count = pocketCount(unit);
        var plural = count && count > 1;
        var countPrefix = plural ? count + ' ' : '';
        var label = countPrefix + (prefix ? prefix + ' ' : '') + t.type + (plural ? ' Pockets' : ' Pocket');
        claims.push({
          key: 'pocket_' + t.type.toLowerCase(),
          label: label,
          icon: ICONS.pocket
        });
      });
    });

    // No specifically-named type (gel/phone/key/card) but still a genuine
    // pocket feature — one generic claim rather than none.
    if (!claims.length) {
      var prefix = pocketModifierPrefix(fullText);
      claims.push({ key: 'pocket_storage', label: (prefix ? prefix + ' ' : '') + 'Storage Pocket', icon: ICONS.pocket });
    }

    return claims.slice(0, maxClaims);
  }

  function extractUSPs(bodyHtml, topN) {
    topN = topN || 3;
    var fullText = stripHtml(bodyHtml);
    var hits = [];
    CATEGORIES.forEach(function (cat) {
      var result = cat.test(fullText);
      if (result) hits.push({ key: cat.key, score: result.score, label: cat.label(result), icon: cat.icon });
    });

    var hasCombo = hits.some(function (h) { return h.key === 'quick_dry_stretch_combo'; });
    var filtered = hasCombo
      ? hits.filter(function (h) { return h.key !== 'quick_dry' && h.key !== 'stretch'; })
      : hits;

    filtered.sort(function (a, b) { return b.score - a.score; });

    // Pockets always win their slots first (strong purchase driver, called
    // out explicitly in the brief) — remaining slots go to the highest-
    // scored fabric/technical claims.
    var pocketClaims = extractPocketClaims(bodyHtml, fullText, topN);
    var remaining = Math.max(0, topN - pocketClaims.length);
    var result = pocketClaims.concat(filtered.slice(0, remaining)).slice(0, topN);

    // Always fill to topN when the description supports it — backfill
    // from the softer FALLBACK_CATEGORIES rather than showing fewer
    // cells, since a short/generic description can otherwise fall short
    // of 3 once weight/material are held to a real textual bar.
    if (result.length < topN) {
      FALLBACK_CATEGORIES.forEach(function (cat) {
        if (result.length >= topN) return;
        var r = cat.test(fullText);
        if (r) result.push({ key: cat.key, score: r.score, label: cat.label(r), icon: cat.icon });
      });
    }

    return result.slice(0, topN);
  }

  function render(container, claims) {
    if (!claims.length) return; // .product-usps__row:empty hides itself via CSS
    var row = container.querySelector('[data-product-usps-row]');
    if (!row) return;
    var html = '';
    claims.forEach(function (c) {
      html += '<div class="product-usps__cell">' +
        '<span class="product-usps__icon">' + (c.icon || GENERIC_ICON) + '</span>' +
        '<span class="product-usps__label">' + c.label + '</span>' +
        '</div>';
    });
    row.innerHTML = html;
  }

  // Merchant-authored labels (metafield/override path) still need an icon
  // — match the same category dictionary (pockets included) against the
  // label text itself, falling back to the generic fabric icon when
  // nothing matches (per the brief: never skip a cell for lack of an
  // icon). A multi-sport/"built for X, Y & Z" claim (e.g. "Built for
  // Swim, Bike & Run") doesn't fit any fabric/construction category, so
  // it's checked separately: two or more sport/movement verbs named in
  // the same label reads as an action claim, not a material one.
  var ACTION_RE = /\b(swim|bike|cycle|run|train)\b.*\b(swim|bike|cycle|run|train)\b/i;
  function iconForLabel(label) {
    if (POCKET_RE.test(label)) return ICONS.pocket;
    for (var i = 0; i < CATEGORIES.length; i++) {
      if (CATEGORIES[i].test(label)) return CATEGORIES[i].icon;
    }
    if (ACTION_RE.test(label)) return ICONS.action;
    return GENERIC_ICON;
  }

  // Builds icons / auto-extracted claims. Runs on load AND for every
  // .product-usps the theme swaps in later (the Tee / Tee Bundle rebuild
  // their gallery — which contains this strip — on each colour change;
  // without this the new strip stayed empty and the row vanished).
  function buildUsps(root) {
  root.querySelectorAll('[data-product-usps-icon-slot]:not([data-nt-built])').forEach(function (slot) {
    slot.setAttribute('data-nt-built', '');
    slot.innerHTML = iconForLabel(slot.getAttribute('data-label') || '');
  });

  root.querySelectorAll('[data-product-usps-auto]:not([data-nt-built])').forEach(function (container) {
    container.setAttribute('data-nt-built', '');
    var sourceEl = container.parentElement.querySelector('[data-product-usps-source]') ||
      document.querySelector('[data-product-usps-source]');
    if (!sourceEl) return;
    var bodyHtml;
    try {
      bodyHtml = JSON.parse(sourceEl.textContent);
    } catch (e) {
      return;
    }
    if (!bodyHtml) return;
    var topN = parseInt(container.getAttribute('data-product-usps-count'), 10) || 3;
    var claims = extractUSPs(bodyHtml, topN);
    render(container, claims);
  });
  }
  buildUsps(document);

  // Layout, not content: keeps the strip matched to the product image's
  // ACTUAL rendered box (width AND left offset), so "centered under the
  // image" is real centering relative to the image specifically — not
  // centering within the wider grid column that contains it (which, with
  // only max-width + margin:auto, collapses to content-width and centers
  // in the FULL column instead, since auto margins take the item out of
  // flex-stretch sizing — confirmed via measurement before fixing). The
  // image's box is computed by the theme's own carousel JS (aspect-ratio
  // driven) rather than a fixed CSS percentage, so there's no pure-CSS way
  // to mirror it — has to be read at runtime. Runs regardless of
  // metafield vs. auto-extraction path.
  //
  // .product-usps now renders as a DESCENDANT of the theme's own
  // .product-desktop-media / .product-mobile-media (each marked with
  // data-product-media-container) rather than behind a separate wrapper —
  // deliberately, so it extends that element's own fit-content box instead
  // of sitting in a new flex container that breaks its position:sticky
  // sizing (the cause of the scroll-triggered overlap this replaced).
  // That means there are now two .product-usps instances in the DOM (one
  // per breakpoint version, both server-rendered, one CSS-hidden) — each
  // synced independently against the image inside its OWN local container,
  // via closest([data-product-media-container]) rather than a shared
  // ancestor. The non-zero-width check stays as a defensive filter in case
  // a container ever holds more than one image-wrapper candidate.
  function syncUspsWidthToImage() {
    document.querySelectorAll('[data-product-usps]').forEach(function (uspsEl) {
      var mediaWrapper = uspsEl.closest('[data-product-media-container]');
      if (!mediaWrapper) return;
      var candidates = mediaWrapper.querySelectorAll('.product-media__image-wrapper');
      var imageViewport = null;
      for (var i = 0; i < candidates.length; i++) {
        if (candidates[i].getBoundingClientRect().width > 0) {
          imageViewport = candidates[i];
          break;
        }
      }
      if (!imageViewport) return;
      var imgRect = imageViewport.getBoundingClientRect();
      var wrapperRect = mediaWrapper.getBoundingClientRect();
      if (imgRect.width <= 0) return;
      uspsEl.style.width = imgRect.width + 'px';
      uspsEl.style.maxWidth = imgRect.width + 'px';
      uspsEl.style.marginLeft = (imgRect.left - wrapperRect.left) + 'px';
      uspsEl.style.marginRight = 'auto';
    });
  }

  var uspsResizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(uspsResizeTimer);
    uspsResizeTimer = setTimeout(syncUspsWidthToImage, 150);
  });
  window.addEventListener('load', syncUspsWidthToImage);
  setTimeout(syncUspsWidthToImage, 400);
  syncUspsWidthToImage();

  new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var added = records[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if ((n.matches && n.matches('[data-product-usps]')) || (n.querySelector && n.querySelector('[data-product-usps]'))) {
          buildUsps(document);
          syncUspsWidthToImage();
          setTimeout(syncUspsWidthToImage, 150);
          setTimeout(syncUspsWidthToImage, 500);
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });
})();
